// The admin bell (0278). Every row opens what it is about — a booking, an SOS
// on the map, the cash or ID queue — and marks itself read on the way.
//
// Hierarchy, top to bottom:
//   · "Needs attention" — UNREAD urgent rows (SOS, payment issue, no-show,
//     running late), pinned so newer routine news cannot bury them
//   · "Latest" — everything else, newest first
// The badge turns red while anything urgent is unread.

import { useState } from "react";
import {
  BellIcon,
  CaretRightIcon,
  CheckIcon,
  ClockIcon,
  InfoIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import type { NotificationLevel } from "@bednbody/logic";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@bednbody/ui/components/empty";
import { Popover, PopoverContent, PopoverTrigger } from "@bednbody/ui/components/popover";
import { Tabs, TabsList, TabsTrigger } from "@bednbody/ui/components/tabs";
import { formatTimeAgo } from "@bednbody/ui/lib/format";
import { cn } from "@bednbody/ui/lib/utils";
import {
  useMarkAllNotificationsRead,
  useNotifications,
  useOpenNotification,
} from "./notifications.hooks";
import { type AdminNotification, sortForBell } from "./notifications.model";

const LEVEL_STYLE: Record<NotificationLevel, { icon: typeof WarningIcon; mark: string }> = {
  urgent: { icon: WarningIcon, mark: "bg-destructive/10 text-destructive" },
  action: { icon: ClockIcon, mark: "bg-warning/15 text-warning" },
  info: { icon: InfoIcon, mark: "bg-muted text-muted-foreground" },
};

export function NotificationBell() {
  const { data: notifications } = useNotifications();
  const markAll = useMarkAllNotificationsRead();
  const openNotification = useOpenNotification();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"unread" | "all">("unread");

  const all = sortForBell(notifications ?? []);
  const unread = all.filter((row) => !row.readAt);
  const shown = tab === "unread" ? unread : all;
  const pinned = shown.filter((row) => !row.readAt && row.level === "urgent");
  const rest = shown.filter((row) => !pinned.includes(row));
  const urgentUnread = unread.some((row) => row.level === "urgent");

  function openRow(row: AdminNotification) {
    setOpen(false);
    openNotification(row);
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger
        render={
          <Button
            aria-label={
              unread.length > 0
                ? `Open notifications, ${unread.length} unread`
                : "Open notifications"
            }
            className="relative ml-auto"
            size="icon"
            variant="outline"
          >
            <BellIcon aria-hidden="true" weight="fill" />
            {unread.length > 0 ? (
              <Badge
                className="-top-2 -translate-x-1/2 absolute left-full min-w-5 px-1"
                variant={urgentUnread ? "destructive" : "default"}
              >
                {unread.length > 99 ? "99+" : unread.length}
              </Badge>
            ) : null}
          </Button>
        }
      />
      <PopoverContent align="end" className="max-h-[var(--available-height)] w-96 max-w-[calc(100vw-2rem)] gap-0 overflow-hidden p-0">
        <div className="flex shrink-0 items-center justify-between gap-2 px-4 pt-4 pb-3">
          <h2 className="font-semibold text-foreground">Notifications</h2>
          <Button
            disabled={unread.length === 0 || markAll.isPending}
            onClick={() => markAll.mutate()}
            size="sm"
            variant="ghost"
          >
            <CheckIcon data-icon="inline-start" />
            Mark all read
          </Button>
        </div>

        <Tabs
          className="shrink-0 px-4 pb-3"
          onValueChange={(value) => setTab(value as "unread" | "all")}
          value={tab}
        >
          <TabsList className="w-full">
            <TabsTrigger className="flex-1" value="unread">
              Unread{unread.length > 0 ? ` · ${unread.length}` : ""}
            </TabsTrigger>
            <TabsTrigger className="flex-1" value="all">
              All
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {shown.length === 0 ? (
          <Empty className="border-t border-border py-10">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <BellIcon />
              </EmptyMedia>
              <EmptyTitle>
                {tab === "unread" ? "You're all caught up" : "No notifications"}
              </EmptyTitle>
              <EmptyDescription>
                Urgent alerts and things waiting on you show up here.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="min-h-0 max-h-[28rem] overflow-y-auto overscroll-contain border-t border-border">
            {pinned.length > 0 ? (
              <NotificationGroup label="Needs attention" onOpen={openRow} rows={pinned} />
            ) : null}
            {rest.length > 0 ? (
              <NotificationGroup
                label={pinned.length > 0 ? "Latest" : null}
                onOpen={openRow}
                rows={rest}
              />
            ) : null}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function NotificationGroup({
  label,
  rows,
  onOpen,
}: {
  label: string | null;
  rows: readonly AdminNotification[];
  onOpen: (row: AdminNotification) => void;
}) {
  return (
    <section aria-label={label ?? "Notifications"} className="p-2">
      {label ? (
        <h3 className="px-2 pt-1 pb-2 text-xs font-semibold text-muted-foreground">{label}</h3>
      ) : null}
      <ul className="space-y-1">
        {rows.map((row) => (
          <li key={row.id}>
            <NotificationRow onOpen={onOpen} row={row} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function NotificationRow({
  row,
  onOpen,
}: {
  row: AdminNotification;
  onOpen: (row: AdminNotification) => void;
}) {
  const style = LEVEL_STYLE[row.level];
  const Icon = style.icon;
  const isUnread = !row.readAt;

  return (
    <button
      className={cn(
        "group flex w-full items-start gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none",
        isUnread && row.level === "urgent" && "bg-destructive/5",
      )}
      onClick={() => onOpen(row)}
      type="button"
    >
      <span
        aria-hidden="true"
        className={cn("mt-0.5 grid size-8 shrink-0 place-items-center rounded-full", style.mark)}
      >
        <Icon className="size-4" weight="fill" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span
            className={cn(
              "truncate text-sm text-foreground",
              isUnread ? "font-semibold" : "font-medium",
            )}
          >
            {row.title}
          </span>
          {isUnread ? (
            <span aria-label="Unread" className="size-2 shrink-0 rounded-full bg-primary" />
          ) : null}
        </span>
        <span className="line-clamp-2 text-sm text-muted-foreground">{row.body}</span>
        <span className="text-xs text-muted-foreground">{formatTimeAgo(row.createdAt)}</span>
      </span>
      <CaretRightIcon
        aria-hidden="true"
        className="mt-2 size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
      />
    </button>
  );
}
